import React, { Component } from "react";
import PropTypes from "prop-types";
import { AdvancedCombobox, Card } from "@evertrue/et-components";
import numeral from "numeral";
import { FISCAL_YEAR } from "../utils/constants";
import moment from "moment";
import { PromiseResolver } from "@evertrue/promise-resolver";
import PoolReportKpiSection from "./pool-report-kpi-section";
import PoolReportStats from "./pool-report-stats";
import PoolActivityReport from "./pool-activity-report";
import PoolDataResolver from "../contexts/pool-data-resolver";
import StageReport from "./stage-report";
import { WithPromises } from "../contexts/api-context";
import MixPanelContext from "../contexts/mixpanel-context";
import RespondTo from "./respond-to";
import { IsMobileProvider } from "../contexts/is-mobile-context";

const getDaysUntil = end => {
  const end_moment = moment(end);
  const start = moment();

  const diff = end_moment.diff(start, "days") + 1;
  return Math.sign(diff) === -1 ? 0 : diff;
};

const DATE_FORMAT = "YYYY-MM-DD";
const options = [
  { label: "All Pool Performance", value: "all" },
  { label: "Unassigned", value: false },
  { label: "Assigned", value: true },
];

class PoolReportController extends Component {
  static defaultProps = {
    pool: {},
  };

  static propTypes = {
    pool: PropTypes.object,
    orgFiscalYearEnd: PropTypes.string,
    promises: PropTypes.object.isRequired,
    hasStagesFeature: PropTypes.bool,
    stagesForGroup: PropTypes.array,
    isMobile: PropTypes.bool,
  };

  state = { selected: options[0] };

  render() {
    const { pool: { assigned_prospect_rate }, pool, orgFiscalYearEnd, promises, isMobile } = this.props;

    return (
      <IsMobileProvider isMobile={isMobile}>
        <PoolDataResolver
          orgFiscalYearEnd={orgFiscalYearEnd}
          poolId={pool.id}
          render={({ activityDateParameters, daysLeft }) => {
            const { value: assigned } = options.find(opt => opt.value === this.state.selected.value);
            return (
              <RespondTo className="pool-report">
                <PoolReportKpiSection
                  daysLeft={daysLeft}
                  solicitorsTotal={pool.total_solicitor_count}
                  prospectsTotal={pool.total_prospect_count}
                  assignedProspectRate={assigned_prospect_rate}
                />
                <RespondTo
                  component={Card}
                  className="pool-report--performance"
                  header={
                    <RespondTo className="pool-report--perf-title">
                      <MixPanelContext.Consumer>
                        {mixPanelEvents => (
                          <AdvancedCombobox
                            options={options}
                            value={options.find(opt => opt.value === this.state.selected.value)}
                            onChange={selected => {
                              this.setState({ selected });
                              mixPanelEvents.toggleAssignmentStatus({ status: selected.value });
                            }}
                          />
                        )}
                      </MixPanelContext.Consumer>
                    </RespondTo>
                  }
                >
                  <RespondTo className="pool-report--performance-main">
                    {pool.giving_category_label === "EverTrueNoData" ? (
                      <div className="pool-report--no-giving">No Giving Data</div>
                    ) : (
                      <PromiseResolver
                        args={assigned}
                        promise={() => promises.fetchPoolById(pool.id, true, assigned)}
                        render={({ data: filteredPool, loading }) => {
                          // we refetch the pool here b/c it takes a query param of assigned: true || false
                          // this filters total_dollars_raised
                          // The pool prop in this component is passed in by flux and we don't need it to know about assigned status
                          const percent_dollars_raised_goal =
                            filteredPool.total_dollars_raised / filteredPool.dollar_amount_goal;
                          const formatted_percent_dollars_raised_goal = numeral(percent_dollars_raised_goal).format(
                            "%0.[00]"
                          );

                          return (
                            <PoolReportStats
                              loading={loading}
                              filteredPool={filteredPool}
                              percent_dollars_raised_goal={percent_dollars_raised_goal}
                              formatted_percent_dollars_raised_goal={formatted_percent_dollars_raised_goal}
                            />
                          );
                        }}
                      />
                    )}
                    {/* have to get dynamicProspectTotal (the divisor) based on unassigned, assigned, all toggle */}

                    <PromiseResolver
                      args={assigned}
                      promise={() => promises.prospectsTotalQuery({ poolId: pool.id, assigned })}
                      render={({ data: { total: dynamicProspectsTotal } }) => (
                        <React.Fragment>
                          <RespondTo
                            className="pool-report--perf-section pool-report--no-pad-section"
                            style={{ flexBasis: "39%" }}
                          >
                            <PoolActivityReport
                              activityDateParameters={activityDateParameters}
                              poolId={pool.id}
                              prospectsTotal={dynamicProspectsTotal}
                              assigned={assigned}
                            />
                          </RespondTo>
                          {this.props.isMobile && !this.props.hasStagesFeature ? null : (
                            <RespondTo className="pool-report--perf-section">
                              <StageReport
                                hasStagesFeature={this.props.hasStagesFeature}
                                poolId={pool.id}
                                assigned={assigned}
                                prospectsTotal={dynamicProspectsTotal}
                                stagesForGroup={this.props.stagesForGroup}
                              />
                            </RespondTo>
                          )}
                        </React.Fragment>
                      )}
                    />
                  </RespondTo>
                </RespondTo>
              </RespondTo>
            );
          }}
        />
      </IsMobileProvider>
    );
  }
}

export default WithPromises(PoolReportController);
